import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import AdminNavbar from './AdminNavbar';
import './AdminOverview.css';

Modal.setAppElement('#root');

const AdminDashboard = () => {
  const [workers, setWorkers] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [report, setReport] = useState(null);
  const [modalData, setModalData] = useState(null);
  const [detailedData, setDetailedData] = useState(null);
  const [loading, setLoading] = useState(false);

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const years = [2023, 2024, 2025, 2026, 2027, 2028, 2029, 2030];

  // Fetch workers list
  useEffect(() => {
    const fetchWorkers = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch('https://missionmap-lc.onrender.com/api/workers', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to fetch workers');
        const data = await res.json();
        setWorkers(data);
      } catch (err) {
        console.error('Error fetching workers:', err);
      }
    };
    fetchWorkers();
  }, []);

  // Fetch report whenever selected worker, month, or year changes
  useEffect(() => {
    if (!selectedWorker) {
      setReport(null);
      return;
    }

    const fetchReport = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(
          `https://missionmap-lc.onrender.com/api/workers/${selectedWorker._id}/report?month=${selectedMonth + 1}&year=${selectedYear}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error('Failed to fetch report');
        const data = await res.json();
        console.log('Report data received:', data);
        setReport(data);
      } catch (err) {
        console.error('Error fetching report:', err);
        setReport(null);
      }
    };

    fetchReport();
  }, [selectedWorker, selectedMonth, selectedYear]);

  // Fetch detailed data for modal
  const fetchDetailedData = async (type, subType) => {
    if (!selectedWorker) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = `https://missionmap-lc.onrender.com/api/workers/${selectedWorker._id}/detailed-report?month=${selectedMonth + 1}&year=${selectedYear}&type=${type}`;
      
      if (subType) {
        url += `&subType=${subType}`;
      }

      console.log('Fetching detailed data from:', url);

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Failed to fetch detailed data');
      const data = await res.json();
      console.log('Detailed data received:', data);
      setDetailedData(data);
    } catch (err) {
      console.error('Error fetching detailed data:', err);
      setDetailedData(null);
    } finally {
      setLoading(false);
    }
  };

  const openModal = async (title, type, subType) => {
    setModalData({ title, type, subType });
    await fetchDetailedData(type, subType);
  };

  const closeModal = () => {
    setModalData(null);
    setDetailedData(null);
  };

  // Helper function to calculate total hours from detailed data
  const calculateTotalHoursFromData = (data) => {
    if (!data || data.length === 0) return '0 hrs';
    
    const totalMinutes = data.reduce((sum, item) => {
      if (item.totalHours && item.totalHours !== '00:00') {
        const [hours, minutes] = item.totalHours.split(':').map(Number);
        return sum + (hours * 60) + minutes;
      }
      return sum;
    }, 0);
    
    const totalHours = Math.floor(totalMinutes / 60);
    const remainingMinutes = totalMinutes % 60;
    
    return `${totalHours} hrs ${remainingMinutes} mins`;
  };

  // Helper function to calculate overtime from detailed data
  const calculateOvertimeFromData = (data) => {
    if (!data || data.length === 0) return '0 hrs';
    
    const totalMinutes = data.reduce((sum, item) => {
      if (item.overtime && item.overtime !== '00:00') {
        const [hours, minutes] = item.overtime.split(':').map(Number);
        return sum + (hours * 60) + minutes;
      }
      return sum;
    }, 0);
    
    const totalHours = Math.floor(totalMinutes / 60);
    const remainingMinutes = totalMinutes % 60;
    
    return `${totalHours} hrs ${remainingMinutes} mins`;
  };

  const renderModalContent = () => {
    if (!modalData) return null;
    if (loading) return <div className="loading">Loading...</div>;
    if (!detailedData || detailedData.length === 0) return <p>No records found.</p>;

    switch (modalData.type) {
      case 'attendance':
        return renderAttendanceModal(modalData.subType);
      case 'leave':
        return renderLeaveModal(modalData.subType);
      case 'tasks':
        return renderTaskModal(modalData.subType);
      case 'projects':
        return renderProjectModal(modalData.subType);
      default:
        return null;
    }
  };

  const renderAttendanceModal = (subType) => {
    if (!detailedData) return <p>No records found.</p>;

    if (subType === 'totalHours') {
      const totalHours = calculateTotalHoursFromData(detailedData);
      
      return (
        <div className="modal-table-container">
          <div className="total-summary">
            <h4>Total Hours for {months[selectedMonth]} {selectedYear}: <strong>{totalHours}</strong></h4>
          </div>
          <table className="detailed-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Total Hours</th>
              </tr>
            </thead>
            <tbody>
              {detailedData.map((day, index) => (
                <tr key={index}>
                  <td>{new Date(day.date).toLocaleDateString()}</td>
                  <td>{day.totalHours || '00:00'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (subType === 'overtime') {
      const totalOvertime = calculateOvertimeFromData(detailedData);
      
      return (
        <div className="modal-table-container">
          <div className="total-summary">
            <h4>Total Overtime for {months[selectedMonth]} {selectedYear}: <strong>{totalOvertime}</strong></h4>
          </div>
          <table className="detailed-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Overtime Hours</th>
                <th>Total Hours</th>
                <th>Sessions</th>
              </tr>
            </thead>
            <tbody>
              {detailedData.map((item, index) => (
                <tr key={index}>
                  <td>{new Date(item.date).toLocaleDateString()}</td>
                  <td>{item.overtime || '00:00'}</td>
                  <td>{item.totalHours || '00:00'}</td>
                  <td>
                    {item.sessions && item.sessions.map((session, i) => (
                      <div key={i} className="session-info">
                        {session.loginTime} - {session.logoutTime || 'Active'}
                      </div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    const headers = {
      present: ['Date', 'Login Time', 'Logout Time', 'Total Hours', 'Sessions'],
      absent: ['Date', 'Status'],
    }[subType] || ['Date', 'Details'];

    return (
      <div className="modal-table-container">
        <table className="detailed-table">
          <thead>
            <tr>{headers.map((h, i) => <th key={i}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {detailedData.map((item, index) => (
              <tr key={index}>
                <td>{new Date(item.date).toLocaleDateString()}</td>
                {subType === 'present' && (
                  <>
                    <td>{item.loginTime || '-'}</td>
                    <td>{item.logoutTime || '-'}</td>
                    <td>{item.totalHours || '00:00'}</td>
                    <td>
                      {item.sessions && item.sessions.map((session, i) => (
                        <div key={i} className="session-info">
                          {session.loginTime} - {session.logoutTime || 'Active'}
                          {session.duration && ` (${session.duration})`}
                        </div>
                      ))}
                    </td>
                  </>
                )}
                {subType === 'absent' && (
                  <>
                    <td>{item.status}</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderLeaveModal = (subType) => {
    if (!detailedData) return <p>No records found.</p>;

    console.log('Rendering leave modal with data:', detailedData);

    // Different table structure for permission vs regular leave
    if (subType === 'permission') {
      return (
        <div className="modal-table-container">
          <table className="detailed-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>From Time</th>
                <th>To Time</th>
                <th>Reason</th>
                <th>Status</th>
                <th>Duration</th>
              </tr>
            </thead>
            <tbody>
              {detailedData.map((leave, i) => (
                <tr key={i}>
                  <td>{leave.displayDate ? new Date(leave.displayDate).toLocaleDateString() : new Date(leave.fromDate).toLocaleDateString()}</td>
                  <td>{leave.fromTime || '-'}</td>
                  <td>{leave.toTime || '-'}</td>
                  <td>{leave.reason || '-'}</td>
                  <td>
                    <span className={`status-badge status-${leave.status?.toLowerCase()}`}>
                      {leave.status}
                    </span>
                  </td>
                  <td>{leave.duration || 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    } else if (subType === 'leave') {
      // Regular leave table (Full Day leaves)
      return (
        <div className="modal-table-container">
          <table className="detailed-table">
            <thead>
              <tr>
                <th>From Date</th>
                <th>To Date</th>
                <th>Reason</th>
                <th>Status</th>
                <th>Duration</th>
              </tr>
            </thead>
            <tbody>
              {detailedData.map((leave, i) => (
                <tr key={i}>
                  <td>{new Date(leave.fromDate).toLocaleDateString()}</td>
                  <td>{new Date(leave.toDate).toLocaleDateString()}</td>
                  <td>{leave.reason || '-'}</td>
                  <td>
                    <span className={`status-badge status-${leave.status?.toLowerCase()}`}>
                      {leave.status}
                    </span>
                  </td>
                  <td>{leave.duration || 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    } else {
      // Total leaves - show all types
      return (
        <div className="modal-table-container">
          <table className="detailed-table">
            <thead>
              <tr>
                <th>Leave Type</th>
                <th>From Date</th>
                <th>To Date</th>
                <th>From Time</th>
                <th>To Time</th>
                <th>Reason</th>
                <th>Status</th>
                <th>Duration</th>
              </tr>
            </thead>
            <tbody>
              {detailedData.map((leave, i) => (
                <tr key={i}>
                  <td>{leave.leaveType}</td>
                  <td>{leave.displayDate ? new Date(leave.displayDate).toLocaleDateString() : new Date(leave.fromDate).toLocaleDateString()}</td>
                  <td>{leave.toDate ? new Date(leave.toDate).toLocaleDateString() : '-'}</td>
                  <td>{leave.fromTime || '-'}</td>
                  <td>{leave.toTime || '-'}</td>
                  <td>{leave.reason || '-'}</td>
                  <td>
                    <span className={`status-badge status-${leave.status?.toLowerCase()}`}>
                      {leave.status}
                    </span>
                  </td>
                  <td>{leave.duration || 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
  };

  const renderTaskModal = (subType) => {
    if (!detailedData) return <p>No records found.</p>;

    return (
      <div className="modal-table-container">
        <table className="detailed-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Task Name</th>
              <th>Deadline</th>
              <th>Status</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {detailedData.map((task, i) => (
              <tr key={i}>
                <td>{task.createdAt ? new Date(task.createdAt).toLocaleDateString() : '-'}</td>                
                <td>{task.taskName || task.title}</td>
                <td>{new Date(task.deadline).toLocaleDateString()}</td>
                <td>{task.status}</td>
                <td>{task.description || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderProjectModal = (subType) => {
    if (!detailedData) return <p>No records found.</p>;

    return (
      <div className="modal-table-container">
        <table className="detailed-table">
          <thead>
            <tr>
              <th>Project Name</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>Status</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {detailedData.map((project, i) => (
              <tr key={i}>
                <td>{project.projectName || project.title}</td>
                <td>{project.startDate ? new Date(project.startDate).toLocaleDateString() : '-'}</td>
                <td>{new Date(project.endDate).toLocaleDateString()}</td>
                <td>{project.status}</td>
                <td>{project.description || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="admin-dashboard  ">
      <AdminNavbar />

      <div className="dashboard-content" style={{ marginTop: '80px' }}>
        <div className="workers-section">
          <div className="section-header">
            <h2>Workers List</h2>
            <div className="date-selector">
              <select value={selectedMonth} onChange={e => setSelectedMonth(parseInt(e.target.value))}>
                {months.map((m, i) => <option key={i} value={i}>{m}</option>)}
              </select>
              <select value={selectedYear} onChange={e => setSelectedYear(parseInt(e.target.value))}>
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>

          <div className="workers-grid">
            {workers.map(worker => (
              <div
                key={worker._id}
                className={`worker-card ${selectedWorker?._id === worker._id ? 'selected' : ''}`}
                onClick={() => setSelectedWorker(worker)}
              >
                <div className="worker-avatar">
                  {worker.profileImage ? (
                    <img
                      src={worker.profileImage.replace(/\\/g, '/')}
                      alt={worker.username}
                      className="profile-img"
                    />
                  ) : (
                    <div className="avatar-placeholder">{worker.username?.[0].toUpperCase() || 'U'}</div>
                  )}
                </div>
                <div className="worker-info">
                  <h3>{worker.username}</h3>
                  <p>{worker.role}</p>
                  <span className="department">{worker.team}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {selectedWorker && report && (
          <div className="report-section">
            <div className="report-header">
              <h2>{selectedWorker.username}'s Report - {months[selectedMonth]} {selectedYear}</h2>
              <div className="worker-details">
                <p><strong>Team:</strong> {selectedWorker.team}</p>
                <p><strong>Role:</strong> {selectedWorker.role}</p>
                <p><strong>Work Mode:</strong> {selectedWorker.workMode}</p>
              </div>
            </div>

            {/* Attendance */}
            <div className="report-card">
              <h3>📊 Attendance Summary</h3>
              <div className="stats-grid">
                <div className="stat-item present" onClick={() => openModal("Present Days Details", 'attendance', 'present')}>
                  <span className="stat-value">{report.attendance?.present || 0}</span>
                  <span className="stat-label">Present</span>
                </div>
                <div className="stat-item absent" onClick={() => openModal("Absent Days Details", 'attendance', 'absent')}>
                  <span className="stat-value">{report.attendance?.absent || 0}</span>
                  <span className="stat-label">Absent</span>
                </div>
                <div className="stat-item totalhrs" onClick={() => openModal("Total Hours Breakdown", 'attendance', 'totalHours')}>
                  <span className="stat-value">{report.attendance?.totalHours || '0'}</span>
                  <span className="stat-label">Total Hours</span>
                </div>
                <div className="stat-item overtime" onClick={() => openModal("Overtime Details", 'attendance', 'overtime')}>
                  <span className="stat-value">{report.attendance?.overtime || '0'}</span>
                  <span className="stat-label">Overtime Hours</span>
                </div>
              </div>
            </div>

            {/* Leave - UPDATED with proper counts */}
            <div className="report-card">
              <h3>🏖️ Leave Summary</h3>
              <div className="stats-grid">
                <div className="stat-item leave" onClick={() => openModal("Leave Details", 'leave', 'leave')}>
                  <span className="stat-value">{report.leave?.leave || 0}</span>
                  <span className="stat-label">Leave</span>
                </div>
                <div className="stat-item permission" onClick={() => openModal("Permission Details", 'leave', 'permission')}>
                  <span className="stat-value">{report.leave?.permission || 0}</span>
                  <span className="stat-label">Permission</span>
                </div>
                <div className="stat-item total" onClick={() => openModal("Total Leave Summary", 'leave', 'total')}>
                  <span className="stat-value">{report.leave?.total || 0}</span>
                  <span className="stat-label">Total</span>
                </div>
              </div>
            </div>

            {/* Tasks */}
            <div className="report-card">
              <h3>✅ Task Summary</h3>
              <div className="stats-grid">
                <div className="stat-item assigned" onClick={() => openModal("Assigned Tasks", 'tasks', 'assigned')}>
                  <span className="stat-value">{report.tasks?.assigned || 0}</span>
                  <span className="stat-label">Assigned</span>
                </div>
                <div className="stat-item completed" onClick={() => openModal("Completed Tasks", 'tasks', 'completed')}>
                  <span className="stat-value">{report.tasks?.completed || 0}</span>
                  <span className="stat-label">Completed</span>
                </div>
                <div className="stat-item pending" onClick={() => openModal("Pending Tasks", 'tasks', 'pending')}>
                  <span className="stat-value">{report.tasks?.pending || 0}</span>
                  <span className="stat-label">Pending</span>
                </div>
              </div>
            </div>

            {/* Projects - UPDATED with proper counts */}
            <div className="report-card">
              <h3>🚀 Project Summary</h3>
              <div className="stats-grid">
                <div className="stat-item ongoing" onClick={() => openModal("Ongoing Projects", 'projects', 'ongoing')}>
                  <span className="stat-value">{report.projects?.ongoing || 0}</span>
                  <span className="stat-label">Ongoing</span>
                </div>
                <div className="stat-item onhold" onClick={() => openModal("On Hold Projects", 'projects', 'onhold')}>
                  <span className="stat-value">{report.projects?.onhold || 0}</span>
                  <span className="stat-label">On Hold</span>
                </div>
                <div className="stat-item completed" onClick={() => openModal("Completed Projects", 'projects', 'completed')}>
                  <span className="stat-value">{report.projects?.completed || 0}</span>
                  <span className="stat-label">Completed</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {!selectedWorker && (
          <div className="select-worker-prompt">
            <div className="prompt-content">
              <h3>👆 Select a Worker</h3>
              <p>Click a worker to view monthly details</p>
            </div>
          </div>
        )}
      </div>

      {modalData && (
        <Modal
          isOpen={!!modalData}
          onRequestClose={closeModal}
          className="report-modal"
          overlayClassName="modal-overlay"
        >
          <div className="modal-header">
            <h2>{modalData.title}</h2>
            <button className="close-btn" onClick={closeModal}>✕</button>
          </div>
          <div className="modal-content">
            {renderModalContent()}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AdminDashboard;